'use client'

import { cn } from '@brew/ui/cn'
import { useCallback } from 'react'

/**
 * "Golden hour" — a live sky gradient exported from FeralUI, ported to a
 * React 19 ref callback (setup on mount, teardown via the returned cleanup)
 * so it needs no effect. The grain and the WebGL context are only set up as
 * the panel nears the view; the program starts once it scrolls into view,
 * runs while it is on screen and the tab is visible (capped on touch
 * devices, see `COARSE_RENDER_SCALE`), and holds a still frame under
 * reduced motion. Without WebGL the CSS gradient underneath (the palette's
 * own stops) stays as the background.
 *
 * Every FeralUI sky export shares this one shader; an export differs only in
 * its palette, its sliders and the frame it starts on, which is a
 * `SkyPreset`.
 */

type Rgb = readonly [number, number, number]

export type SkyPreset = {
  /** The palette as CSS stops: the no-WebGL fallback gradient. */
  stops: readonly [string, string, string, string]
  /** The shader's four tones (0–1 RGB). */
  tones: { main: Rgb; low: Rgb; mid: Rgb; high: Rgb }
  /** The export's sliders (0–100) and its direction (quarter turns). */
  scale: number
  distortion: number
  swirl: number
  direction: number
  /** The frame the export starts on. */
  startT: number
}

/** 0–255 RGB as the shader's 0–1 tone. */
function tone(r: number, g: number, b: number): Rgb {
  return [r / 255, g / 255, b / 255]
}

/** The FeralUI exports the home page uses. */
export const SKY_PRESETS = {
  /** The prompt panel's sky: cream into a burnt-orange horizon. */
  goldenHour: {
    stops: ['#FFF3D6', '#FFD9A0', '#FFAE66', '#E07A52'],
    tones: {
      main: tone(255, 217, 160),
      low: tone(224, 122, 82),
      mid: tone(255, 174, 102),
      high: tone(255, 243, 214),
    },
    scale: 88,
    distortion: 100,
    swirl: 45,
    direction: 2,
    startT: 32.902_368_487_999_89,
  },
  /** Cream into marigold, early in its drift. */
  marigold: {
    stops: ['#FFEDDE', '#FFB342', '#FFAE66', '#FFC88A'],
    tones: {
      main: tone(255, 200, 138),
      low: tone(255, 174, 102),
      mid: tone(255, 179, 66),
      high: tone(255, 237, 222),
    },
    scale: 100,
    distortion: 100,
    swirl: 61,
    direction: 0,
    startT: 25.214_568_487_999_678,
  },
  /** Apricot and honey, turned a quarter. */
  apricot: {
    stops: ['#FFC089', '#FFD9A0', '#FFAE66', '#FFC574'],
    tones: {
      main: tone(255, 197, 116),
      low: tone(255, 174, 102),
      mid: tone(255, 192, 137),
      high: tone(255, 217, 160),
    },
    scale: 88,
    distortion: 100,
    swirl: 45,
    direction: 1,
    startT: 39.147_334_975_998_98,
  },
  /** The marigold palette later in its drift: a different sky, same light. */
  marigoldLate: {
    stops: ['#FFEDDE', '#FFB342', '#FFAE66', '#FFC88A'],
    tones: {
      main: tone(255, 200, 138),
      low: tone(255, 174, 102),
      mid: tone(255, 179, 66),
      high: tone(255, 237, 222),
    },
    scale: 100,
    distortion: 100,
    swirl: 61,
    direction: 0,
    startT: 53.958_038_839_998_466,
  },
} as const satisfies Record<string, SkyPreset>

export type SkyPresetName = keyof typeof SKY_PRESETS

const VERTEX_SHADER = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}'

const FRAGMENT_SHADER = `precision highp float;
uniform vec2 u_res;
uniform float u_t;
uniform vec3 u_main;
uniform vec3 u_low;
uniform vec3 u_mid;
uniform vec3 u_high;
uniform float u_wind;
uniform float u_warp;
uniform float u_nscale;
uniform sampler2D u_noise;
uniform vec2 u_dirv;

const float FBM_STRENGTH = 0.912;
const float BLUR_RADIUS = 1.2673;
const float ZOOM = 0.3971;
const float GRAIN_SCALE = 2.5;
const float GRAIN_STRENGTH = 0.014;

vec3 burn(vec3 base, vec3 blend, float op){
  return max(base + blend - vec3(1.0), vec3(0.0))*op + base*(1.0-op);
}
float rand2(vec2 n){ return fract(sin(dot(n, vec2(12.9898, 4.1414)))*43758.5453); }
float noise2(vec2 p){
  vec2 ip = floor(p); vec2 u = fract(p);
  u = u*u*(3.0-2.0*u);
  float m = mix(mix(rand2(ip), rand2(ip+vec2(1.,0.)), u.x), mix(rand2(ip+vec2(0.,1.)), rand2(ip+vec2(1.,1.)), u.x), u.y);
  return m*m;
}
float fbm4(vec2 x){
  float v = 0.0; float a = 0.5;
  vec2 shift = vec2(100.0);
  mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.5));
  for (int i = 0; i < 4; i++) { v += a*noise2(x); x = rot*x*2.0 + shift; a *= 0.5; }
  return v;
}
vec4 permute4(vec4 x){ return mod((x*34.0 + 1.0)*x, 289.0); }
vec4 tisqrt(vec4 r){ return 1.79284291400159 - 0.85373472095314*r; }
vec3 fade3(vec3 t){ return t*t*t*(t*(t*6.0-15.0)+10.0); }
float cnoise(vec3 P){
  vec3 Pi0 = floor(P); vec3 Pi1 = Pi0 + vec3(1.0);
  Pi0 = mod(Pi0, 289.0); Pi1 = mod(Pi1, 289.0);
  vec3 Pf0 = fract(P); vec3 Pf1 = Pf0 - vec3(1.0);
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = vec4(Pi0.z); vec4 iz1 = vec4(Pi1.z);
  vec4 ixy = permute4(permute4(ix) + iy);
  vec4 ixy0 = permute4(ixy + iz0); vec4 ixy1 = permute4(ixy + iz1);
  vec4 gx0 = ixy0/7.0; vec4 gy0 = fract(floor(gx0)/7.0) - 0.5; gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0); vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0*(step(vec4(0.0), gx0) - 0.5); gy0 -= sz0*(step(vec4(0.0), gy0) - 0.5);
  vec4 gx1 = ixy1/7.0; vec4 gy1 = fract(floor(gx1)/7.0) - 0.5; gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1); vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1*(step(vec4(0.0), gx1) - 0.5); gy1 -= sz1*(step(vec4(0.0), gy1) - 0.5);
  vec3 g000 = vec3(gx0.x, gy0.x, gz0.x); vec3 g100 = vec3(gx0.y, gy0.y, gz0.y);
  vec3 g010 = vec3(gx0.z, gy0.z, gz0.z); vec3 g110 = vec3(gx0.w, gy0.w, gz0.w);
  vec3 g001 = vec3(gx1.x, gy1.x, gz1.x); vec3 g101 = vec3(gx1.y, gy1.y, gz1.y);
  vec3 g011 = vec3(gx1.z, gy1.z, gz1.z); vec3 g111 = vec3(gx1.w, gy1.w, gz1.w);
  vec4 n0 = tisqrt(vec4(dot(g000,g000), dot(g010,g010), dot(g100,g100), dot(g110,g110)));
  g000 *= n0.x; g010 *= n0.y; g100 *= n0.z; g110 *= n0.w;
  vec4 n1 = tisqrt(vec4(dot(g001,g001), dot(g011,g011), dot(g101,g101), dot(g111,g111)));
  g001 *= n1.x; g011 *= n1.y; g101 *= n1.z; g111 *= n1.w;
  float n000 = dot(g000, Pf0); float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z)); float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z)); float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz)); float n111 = dot(g111, Pf1);
  vec3 fx = fade3(Pf0);
  vec4 nz = mix(vec4(n000,n100,n010,n110), vec4(n001,n101,n011,n111), fx.z);
  vec2 ny = mix(nz.xy, nz.zw, fx.y);
  return 2.2*mix(ny.x, ny.y, fx.x);
}
void main(){
  vec2 st = gl_FragCoord.xy/u_res - 0.5;
  st.x *= u_res.x/u_res.y;
  st = mat2(u_dirv.x, u_dirv.y, -u_dirv.y, u_dirv.x)*st;
  float time = u_t*0.85;
  vec2 uv = st*(1.0/(2.0*ZOOM)) + 0.5;
  float noiseX = cnoise(vec3(uv*u_nscale + vec2(0.0, 74.8572), time*0.3));
  float noiseY = cnoise(vec3(uv*u_nscale + vec2(203.91282, 10.0), time*0.3));
  uv += vec2(noiseX*2.0, noiseY)*u_warp;
  float noiseA = cnoise(vec3(uv*18.0 + vec2(344.91282, 0.0), time*0.3))
               + cnoise(vec3(uv*39.6 + vec2(723.937, 0.0), time*0.4))*0.5;
  uv += noiseA*0.02;
  uv.y -= 0.09;
  float xf = (sin(time) + 1.0)*0.5;
  vec2 texUv = uv*GRAIN_SCALE;
  float d0 = mix(texture2D(u_noise, texUv).r - 0.5, texture2D(u_noise, vec2(texUv.x, 1.0-texUv.y)).g - 0.5, xf)*GRAIN_STRENGTH;
  texUv += vec2(63.861, 368.937);
  float d1 = mix(texture2D(u_noise, texUv).r - 0.5, texture2D(u_noise, vec2(texUv.x, 1.0-texUv.y)).g - 0.5, xf)*GRAIN_STRENGTH;
  texUv += vec2(453.163, 1649.808);
  float d3 = mix(texture2D(u_noise, texUv).r - 0.5, texture2D(u_noise, vec2(texUv.x, 1.0-texUv.y)).g - 0.5, xf)*GRAIN_STRENGTH;
  uv += d0;
  vec2 stF = uv*u_nscale;
  vec2 q = vec2(fbm4(stF*0.5 + u_wind*time));
  vec2 r = vec2(fbm4(stF + q + vec2(0.3, 9.2) + 0.15*time), fbm4(stF + q + vec2(8.3, 0.8) + 0.126*time));
  float fv = fbm4(stF + r - q);
  float full = (fv + 0.6*fv*fv + 0.7*fv + 0.5)*0.5;
  full = pow(full, 0.55)*FBM_STRENGTH;
  float blurR = BLUR_RADIUS*1.5;
  vec2 uvA = uv + vec2((full-0.5)*1.2) + vec2(0.0, 0.025) + d0;
  float snA = noise2(uvA*2.0 + vec2(0.0, time*0.5))*3.0;
  float lA = pow(smoothstep(snA - 1.2*blurR, snA + 1.2*blurR, (uvA.y - 0.5)*5.0 + 0.5), 0.8);
  vec2 uvB = uv + vec2((full-0.5)*0.85) + vec2(0.0, 0.025) + d1;
  float snB = noise2(uvB*4.0 + vec2(293.0, time))*2.8;
  float lB = pow(smoothstep(snB - 0.9*blurR, snB + 0.9*blurR, (uvB.y - 0.6)*5.0 + 0.5), 0.9);
  vec2 uvC = uv + vec2((full-0.5)*1.1) + d3;
  float snC = noise2(uvC*6.0 + vec2(153.0, time*1.2))*2.6;
  float lC = smoothstep(snC - 0.7*blurR, snC + 0.7*blurR, (uvC.y - 0.9)*6.0 + 0.5);
  vec3 col = burn(u_main, u_low, 1.0 - lA);
  col = burn(col, mix(u_main, u_mid, 1.0 - lB), lA);
  col = mix(col, mix(u_main, u_high, 1.0 - lC), lA*lB);
  gl_FragColor = vec4(col, 1.0);
}`

/** The export's speed and film grain, shared by every preset. */
const SPEED = 61
const GRAIN = 67
const FLOW_SPEED_RATE = 1.2

/**
 * The sky is soft everywhere, so it renders at one sample per CSS pixel
 * (never the device ratio) and at most this wide — a quarter of the fill
 * cost on a 2× screen with no visible difference.
 */
const MAX_RENDER_WIDTH = 1600

/**
 * Phones and tablets (a coarse primary pointer) draw the sky at half its CSS
 * size and at most 30 frames a second. The sky is a slow, blurred drift, so
 * neither shows, while the shader's fill cost drops to an eighth: on a
 * mid-range phone the full-rate sky kept the GPU busy enough to delay the
 * frame that answers a tap (INP). The film grain is its own CSS layer at
 * full resolution, so it stays as crisp as before.
 */
const COARSE_POINTER_QUERY = '(pointer: coarse)'
const COARSE_RENDER_SCALE = 0.5
const COARSE_MIN_FRAME_MS = 1000 / 30

/**
 * How far ahead of the viewport a sky sets up its canvas, WebGL context and
 * grain. A page shows several skies; creating every context at hydration
 * was main-thread work no visitor sees until they scroll.
 */
const SETUP_MARGIN = '400px 0px'

type SkyProgram = {
  program: WebGLProgram
  shaders: [WebGLShader, WebGLShader]
  buffer: WebGLBuffer
  texture: WebGLTexture
  resolution: WebGLUniformLocation | null
  time: WebGLUniformLocation | null
}

function compile(
  gl: WebGLRenderingContext,
  kind: number,
  source: string
): WebGLShader {
  const shader = gl.createShader(kind)
  if (!shader) {
    throw new Error('Could not create the sky shader.')
  }
  gl.shaderSource(shader, source)
  gl.compileShader(shader)
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) ?? 'Sky shader failed.'
    gl.deleteShader(shader)
    throw new Error(message)
  }
  return shader
}

function createSkyProgram(
  gl: WebGLRenderingContext,
  preset: SkyPreset
): SkyProgram {
  // Everything allocated so far is released if a later step fails, so a
  // failed setup leaves nothing behind in the context.
  const shaders: Array<WebGLShader> = []
  let ownedProgram: WebGLProgram | null = null
  let ownedBuffer: WebGLBuffer | null = null
  let ownedTexture: WebGLTexture | null = null
  try {
    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER)
    shaders.push(vertex)
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER)
    shaders.push(fragment)
    const program = gl.createProgram()
    const buffer = gl.createBuffer()
    const texture = gl.createTexture()
    ownedProgram = program
    ownedBuffer = buffer
    ownedTexture = texture
    if (!(program && buffer && texture)) {
      throw new Error('Could not allocate the sky program.')
    }

    gl.attachShader(program, vertex)
    gl.attachShader(program, fragment)
    gl.bindAttribLocation(program, 0, 'p')
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(gl.getProgramInfoLog(program) ?? 'Sky program failed.')
    }
    // biome-ignore lint/correctness/useHookAtTopLevel: WebGL's useProgram, not a React hook
    gl.useProgram(program)

    // One oversized triangle covers the viewport.
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    )
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)

    const noise = new Uint8Array(256 * 256 * 4)
    for (let i = 0; i < 256 * 256; i++) {
      const value = Math.floor(Math.random() * 256)
      noise.set([value, value, value, 255], i * 4)
    }
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      256,
      256,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      noise
    )
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

    const uniform = (name: string) => gl.getUniformLocation(program, name)
    gl.uniform1i(uniform('u_noise'), 0)
    gl.uniform3fv(uniform('u_main'), preset.tones.main)
    gl.uniform3fv(uniform('u_low'), preset.tones.low)
    gl.uniform3fv(uniform('u_mid'), preset.tones.mid)
    gl.uniform3fv(uniform('u_high'), preset.tones.high)
    gl.uniform1f(uniform('u_nscale'), 0.35 + (preset.scale / 100) * 1.15)
    gl.uniform1f(uniform('u_warp'), (preset.distortion / 100) * 0.47)
    gl.uniform1f(uniform('u_wind'), (preset.swirl / 100) * 0.36)
    const angle = ((preset.direction % 4) * Math.PI) / 2
    gl.uniform2f(uniform('u_dirv'), Math.cos(angle), Math.sin(angle))

    return {
      program,
      shaders: [vertex, fragment],
      buffer,
      texture,
      resolution: uniform('u_res'),
      time: uniform('u_t'),
    }
  } catch (error) {
    if (ownedTexture) {
      gl.deleteTexture(ownedTexture)
    }
    if (ownedBuffer) {
      gl.deleteBuffer(ownedBuffer)
    }
    if (ownedProgram) {
      gl.deleteProgram(ownedProgram)
    }
    for (const shader of shaders) {
      gl.deleteShader(shader)
    }
    throw error
  }
}

function deleteSkyProgram(gl: WebGLRenderingContext, sky: SkyProgram) {
  gl.deleteTexture(sky.texture)
  gl.deleteBuffer(sky.buffer)
  gl.deleteProgram(sky.program)
  for (const shader of sky.shaders) {
    gl.deleteShader(shader)
  }
}

/** The export's film grain: a 256px noise tile, overlaid at GRAIN / 200. */
function paintGrain(layer: HTMLElement | null) {
  const tile = document.createElement('canvas')
  tile.width = 256
  tile.height = 256
  const context = tile.getContext('2d')
  if (!(layer && context)) {
    return
  }
  const image = context.createImageData(256, 256)
  for (let i = 0; i < image.data.length; i += 4) {
    const value = Math.round((Math.random() + Math.random()) * 127.5)
    image.data.set([value, value, value, 255], i)
  }
  context.putImageData(image, 0, 0)
  layer.style.backgroundImage = `url(${tile.toDataURL()})`
}

/**
 * Sets the sky up in its root; the returned function tears it all down.
 * Each setup draws on a canvas of its own, removed again on teardown: the
 * context is released there, and a released context never comes back, so a
 * remount (React's dev double-mount included) must not reuse the canvas.
 */
function startSky(root: HTMLDivElement, preset: SkyPreset): () => void {
  paintGrain(root.querySelector<HTMLElement>('[data-grain]'))
  const canvas = document.createElement('canvas')
  canvas.className = 'absolute inset-0 block size-full'
  canvas.style.visibility = 'hidden'
  root.prepend(canvas)
  const gl = canvas.getContext('webgl', {
    alpha: false,
    antialias: false,
    powerPreference: 'low-power',
  })
  if (!gl) {
    canvas.remove()
    return () => undefined
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  const isCoarsePointer = window.matchMedia(COARSE_POINTER_QUERY).matches
  const renderScale = isCoarsePointer ? COARSE_RENDER_SCALE : 1
  const minFrameMs = isCoarsePointer ? COARSE_MIN_FRAME_MS : 0
  let sky: SkyProgram | null = null
  let frame = 0
  let last = 0
  let t = preset.startT
  let isVisible = false
  let isLost = false
  // A setup that failed once fails again on the same context: don't retry
  // it on every scroll into view; a restored context gets a fresh try.
  let hasFailed = false

  const hide = () => {
    canvas.style.visibility = 'hidden'
  }

  const paint = () => {
    if (!sky || isLost || canvas.width < 1 || canvas.height < 1) {
      return
    }
    gl.viewport(0, 0, canvas.width, canvas.height)
    gl.uniform2f(sky.resolution, canvas.width, canvas.height)
    gl.uniform1f(sky.time, t)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }

  const resize = () => {
    const rect = canvas.getBoundingClientRect()
    const scale =
      Math.min(1, MAX_RENDER_WIDTH / Math.max(1, rect.width)) * renderScale
    canvas.width = Math.max(1, Math.round(rect.width * scale))
    canvas.height = Math.max(1, Math.round(rect.height * scale))
    paint()
  }

  const start = () => {
    if (sky || isLost || hasFailed) {
      return
    }
    try {
      sky = createSkyProgram(gl, preset)
      canvas.style.visibility = 'visible'
      resize()
    } catch {
      hasFailed = true
      hide()
    }
  }

  const isRunning = () =>
    Boolean(sky) &&
    !isLost &&
    isVisible &&
    !document.hidden &&
    !reducedMotion.matches

  const tick = (now: number) => {
    frame = 0
    if (!isRunning()) {
      last = 0
      return
    }
    // Inside the frame budget: wait for a later frame. `t` then advances by
    // the whole interval, so a capped sky drifts at the same speed.
    if (last && now - last < minFrameMs - 1) {
      frame = requestAnimationFrame(tick)
      return
    }
    if (last) {
      t += Math.min(0.05, (now - last) / 1000) * (SPEED / 100) * FLOW_SPEED_RATE
    }
    last = now
    paint()
    frame = requestAnimationFrame(tick)
  }

  const sync = () => {
    if (isRunning()) {
      if (!frame) {
        last = 0
        frame = requestAnimationFrame(tick)
      }
      return
    }
    cancelAnimationFrame(frame)
    frame = 0
    last = 0
    paint()
  }

  const onLost = (event: Event) => {
    event.preventDefault()
    isLost = true
    sky = null
    sync()
    hide()
  }
  const onRestored = () => {
    isLost = false
    hasFailed = false
    start()
    sync()
  }

  const resizer = new ResizeObserver(resize)
  resizer.observe(canvas)
  const viewport = new IntersectionObserver(([entry]) => {
    isVisible = entry?.isIntersecting ?? false
    if (isVisible) {
      start()
    }
    sync()
  })
  viewport.observe(canvas)
  canvas.addEventListener('webglcontextlost', onLost)
  canvas.addEventListener('webglcontextrestored', onRestored)
  document.addEventListener('visibilitychange', sync)
  reducedMotion.addEventListener('change', sync)

  return () => {
    cancelAnimationFrame(frame)
    resizer.disconnect()
    viewport.disconnect()
    canvas.removeEventListener('webglcontextlost', onLost)
    canvas.removeEventListener('webglcontextrestored', onRestored)
    document.removeEventListener('visibilitychange', sync)
    reducedMotion.removeEventListener('change', sync)
    if (sky && !isLost) {
      deleteSkyProgram(gl, sky)
    }
    sky = null
    // Browsers cap live WebGL contexts per page; hand this one back.
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    canvas.remove()
  }
}

/**
 * Mounts the sky into its root: the CSS gradient (the palette's own stops)
 * shows until the root nears the viewport, then `startSky` sets up the
 * grain and the WebGL canvas. The returned function tears down either state.
 */
function mountSky(root: HTMLDivElement, preset: SkyPreset): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    return startSky(root, preset)
  }
  let teardown: (() => void) | null = null
  const near = new IntersectionObserver(
    ([entry]) => {
      if (entry?.isIntersecting && !teardown) {
        near.disconnect()
        teardown = startSky(root, preset)
      }
    },
    { rootMargin: SETUP_MARGIN }
  )
  near.observe(root)
  return () => {
    near.disconnect()
    teardown?.()
  }
}

/**
 * The animated sky filling its positioned parent, golden hour unless another
 * `preset` is named. Decorative: hidden from assistive tech and from the
 * pointer.
 */
export function GoldenHourSky({
  className,
  preset = 'goldenHour',
}: {
  className?: string
  preset?: SkyPresetName
}) {
  const sky: SkyPreset = SKY_PRESETS[preset]
  // One mount per preset: the ref callback's identity is the remount key.
  const mount = useCallback(
    (root: HTMLDivElement) => mountSky(root, sky),
    [sky]
  )
  return (
    <div
      ref={mount}
      aria-hidden="true"
      className={cn('pointer-events-none absolute inset-0', className)}
      style={{ background: `linear-gradient(180deg, ${sky.stops.join(', ')})` }}
    >
      <div
        data-grain
        className="absolute inset-0 bg-size-[256px_256px] mix-blend-overlay [image-rendering:pixelated]"
        style={{ opacity: GRAIN / 200 }}
      />
    </div>
  )
}
